import { ChevronDown, Info } from "lucide-react";
import React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/common/tooltip";
import { IngredientDrilldown, LifecycleStage } from "../types";

interface LifecycleStageItemProps {
  stage: LifecycleStage;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * LifecycleStageItem Component
 * Single Responsibility: Display individual lifecycle stage with expandable drilldown
 */
export const LifecycleStageItem: React.FC<LifecycleStageItemProps> = ({
  stage,
  isExpanded,
  onToggle
}) => {
  const hasDrilldown = !!stage.drilldown?.length;

  return (
    <div>
      <div
        className={`rounded-lg px-1 tw-py-1 transition-colors ${
          hasDrilldown ? "tw-cursor-pointer hover:tw-bg-muted/40" : ""
        } ${isExpanded ? "tw-bg-muted/30" : ""}`}
        onClick={() => hasDrilldown && onToggle()}
      >
        <div className="tw-flex tw-items-start tw-justify-between tw-mb-1">
          <div className="tw-min-w-0 tw-flex-1 tw-mr-3">
            <div className="tw-flex tw-items-center tw-gap-1.5">
              {hasDrilldown && (
                <ChevronDown
                  className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform duration-200 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              )}
              <span className="tw-text-xs tw-font-medium tw-text-foreground">{stage.label}</span>
              <Tooltip>
                <TooltipTrigger onClick={e => e.stopPropagation()}>
                  <Info className="tw-h-3 tw-w-3 tw-text-muted-foreground/60 hover:tw-text-muted-foreground tw-transition-colors" />
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="tw-max-w-[280px] tw-text-xs tw-whitespace-pre-line tw-leading-relaxed"
                >
                  {stage.tooltip}
                </TooltipContent>
              </Tooltip>
              {hasDrilldown && !isExpanded && (
                <span className="tw-text-[10px] tw-text-muted-foreground tw-ml-1">
                  ({stage.drilldown!.length} ingredients)
                </span>
              )}
            </div>
            <p className="tw-text-[11px] tw-text-muted-foreground tw-mt-0.5 tw-leading-snug">
              {stage.subtitle}
            </p>
          </div>
          <span className="tw-text-xs tw-text-muted-foreground tw-shrink-0 tw-pt-0.5">
            {stage.pct.toFixed(3)}%
          </span>
        </div>
        <div className="tw-h-2.5 tw-rounded-full tw-bg-muted tw-overflow-hidden">
          <div
            className="tw-h-full tw-rounded-full tw-transition-all tw-duration-700 tw-ease-out"
            style={{ width: `${stage.pct}%`, backgroundColor: stage.color }}
          />
        </div>
      </div>

      {/* Ingredient drilldown */}
      {hasDrilldown && isExpanded && (
        <IngredientDrilldownList drilldown={stage.drilldown!} stageColor={stage.color} />
      )}
    </div>
  );
};

/**
 * IngredientDrilldownList Component
 * Single Responsibility: Display ingredient breakdown for a stage
 */
const IngredientDrilldownList: React.FC<{
  drilldown: IngredientDrilldown[];
  stageColor: string;
}> = ({ drilldown, stageColor }) => {
  return (
    <div className="tw-ml-6 tw-mt-1 tw-mb-2 tw-border-l-2 tw-border-primary/20 tw-pl-4 tw-space-y-1.5 tw-animate-in tw-slide-in-from-top-2 tw-duration-200">
      <p className="tw-text-[10px] tw-font-medium tw-text-muted-foreground tw-uppercase tw-tracking-wider tw-pt-1">
        Ingredient breakdown
      </p>
      {drilldown.map((ing, i) => {
        const maxIngPct = drilldown[0].pct;
        const barW = Math.max(6, (ing.pct / maxIngPct) * 100);
        return (
          <div key={ing.name} className="tw-flex tw-items-center tw-gap-3 tw-group">
            <span className="tw-text-[10px] tw-text-muted-foreground tw-w-4 tw-text-right">
              {i + 1}
            </span>
            <div className="tw-flex-1 tw-min-w-0">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-0.5">
                <span className="tw-text-[11px] tw-text-foreground tw-truncate tw-pr-2">
                  {ing.name}
                </span>
                <span className="tw-text-[10px] tw-text-muted-foreground tw-shrink-0">
                  {ing.pct}%
                </span>
              </div>
              <div className="tw-h-1.5 tw-rounded-full tw-bg-muted tw-overflow-hidden">
                <div
                  className="tw-h-full tw-rounded-full tw-transition-all tw-duration-500"
                  style={{
                    width: `${barW}%`,
                    backgroundColor: stageColor,
                    opacity: 1 - i * 0.06
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
